/**
 * Compare page — side-by-side product comparison across up to 4 items.
 *
 * URL: /compare?ids=pid1,pid2,pid3   (ids are shareable — copy the URL to
 * send anyone your comparison).
 *
 * Data: fetches each product from the public `/api/shop/products/{slug}`
 * endpoint. Products can be added by product_id OR slug (we normalise via
 * a small `/api/shop/products?ids=` fallback below).
 */
import React, { useMemo, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueries, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { X, Share2, ShoppingBag, ArrowLeft, Copy, Check, Sparkles } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { ShopShell } from "@/pages/Shop";
import { MiniScoreArc } from "@/components/shared/MiniScoreArc";
import { productImage } from "@/lib/productImage";
import { toast } from "sonner";
import { useCompare } from "@/hooks/useCompare";
import { QRShareModal } from "@/components/shared/QRShareModal";
import { useSibaScoreConfig } from "@/hooks/useSibaScoreRanges";
import { useCurrency } from "@/contexts/CurrencyContext";

const http = axios.create({ baseURL: API_BASE });

const SIGNAL_LABELS: { key: string; label: string; weight: number }[] = [
  { key: "sentiment",    label: "Customer Sentiment", weight: 12 },
  { key: "volume",       label: "Review Volume", weight: 9 },
  { key: "stars",        label: "Star-Rating Consistency", weight: 8 },
  { key: "complaint",    label: "Complaint Rate", weight: 10 },
  { key: "brand",        label: "Verified Brand", weight: 8 },
  { key: "cross",        label: "Cross-Platform Mentions", weight: 10 },
  { key: "endorsements", label: "Creator Endorsements", weight: 9 },
  { key: "returns",      label: "Return Rate", weight: 9 },
  { key: "price",        label: "Price Value", weight: 8 },
  { key: "longevity",    label: "Product Longevity", weight: 8 },
  { key: "support",      label: "Support Responsiveness", weight: 9 },
];

// Deterministic per-signal value so the same product+signal always renders
// the same bar. Mirrors the seeding logic used in Shop PDP.
function signalPct(productId: string, score: number | null, sigIdx: number, label: string): number {
  const raw = ((score ?? 0) / 1000);
  const variance = ((label.length * (sigIdx + 3)) % 20) - 10;
  return Math.max(0, Math.min(100, Math.round(raw * 100 + variance)));
}

export const ComparePage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const { ids: trayIds, remove, clear } = useCompare();
  const nav = useNavigate();

  // URL ?ids= is the source of truth — falls back to the local tray so a
  // fresh visitor navigating from the drawer still sees their picks.
  // We split on `~` (never appears in slugs) so slugs containing commas
  // (e.g. "baby-diapers-—-pants-style,-pack-of-74") survive round-tripping.
  const rawParam = params.get("ids") || "";
  const urlIds = rawParam
    .split(rawParam.includes("~") ? "~" : ",")
    .map((s) => {
      try { return decodeURIComponent(s.trim()); }
      catch { return s.trim(); }
    })
    .filter(Boolean);
  const ids = urlIds.length > 0 ? urlIds : trayIds;

  const queries = useQueries({
    queries: ids.slice(0, 4).map((idOrSlug) => ({
      queryKey: ["compare-product", idOrSlug],
      queryFn: async () => {
        // Try slug first; if not found, look up by product_id via /products?q=
        try {
          const r = await http.get(`/shop/products/${idOrSlug}`);
          return r.data?.product;
        } catch {
          const r2 = await http.get(`/shop/products`, { params: { q: idOrSlug, limit: 1 } });
          return r2.data?.items?.[0] || null;
        }
      },
    })),
  });

  const products = useMemo(
    () => queries.map((q) => q.data).filter((p) => p) as any[],
    [queries],
  );
  const loading = queries.some((q) => q.isLoading);

  const removeAndSync = (idOrSlug: string) => {
    const nextIds = ids.filter((x) => x !== idOrSlug);
    remove(idOrSlug);
    if (urlIds.length > 0) {
      const next = new URLSearchParams(params);
      if (nextIds.length) next.set("ids", nextIds.join(","));
      else next.delete("ids");
      setParams(next, { replace: true });
    }
  };

  const [qrOpen, setQrOpen] = React.useState(false);
  const sibaConfig = useSibaScoreConfig();
  const { formatPrice } = useCurrency();
  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/compare?ids=${products
    .map((p) => encodeURIComponent(p.slug || p.product_id))
    .join("~")}`;
  const shareLink = () => setQrOpen(true);

  return (
    <ShopShell
      title="Compare products — SociBaba"
      description="Side-by-side comparison of SibaScore signals for up to 4 products."
      hideFooter
      showBottomNav
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <button
            onClick={() => nav(-1)}
            data-testid="compare-back"
            className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-display font-medium mb-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <h1 className="font-serif-display font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white tracking-tight">
            Compare {products.length} product{products.length === 1 ? "" : "s"}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Side-by-side across every SibaScore signal. Add up to 4 items at once.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {products.length >= 2 && (
            <button
              onClick={shareLink}
              data-testid="compare-share"
              className="inline-flex items-center gap-1.5 border border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white text-slate-900 dark:text-white text-sm font-display font-semibold px-4 py-2 transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" /> Share link
            </button>
          )}
          {products.length > 0 && (
            <button
              onClick={() => { clear(); nav("/shop"); }}
              data-testid="compare-clear"
              className="inline-flex items-center gap-1.5 border border-rose-200 dark:border-rose-900 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-sm font-display font-semibold px-4 py-2 transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Clear all
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-500 dark:text-slate-400">Loading comparison…</div>
      ) : products.length < 2 ? (
        <EmptyCompareState onBrowse={() => nav("/shop")} />
      ) : (
        <>
        <VerdictBanner ids={products.map((p) => p.slug || p.product_id)} />
        <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <th className="text-left p-4 text-[10px] uppercase tracking-widest font-display font-bold text-slate-400 w-40 sticky left-0 bg-white dark:bg-slate-900">Signal</th>
                {products.map((p) => (
                  <th key={p.product_id} className="p-4 text-left min-w-[200px]" data-testid={`compare-col-${p.slug}`}>
                    <div className="relative">
                      <button
                        onClick={() => removeAndSync(p.slug || p.product_id)}
                        aria-label={`Remove ${p.title}`}
                        className="absolute -top-2 -right-2 h-6 w-6 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-rose-500 z-10"
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="aspect-square bg-slate-50 dark:bg-slate-800 mb-3">
                        <img src={productImage(p as any, 400)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-semibold">{p.brand || "Unbranded"}</div>
                      <Link to={`/shop/p/${p.slug}`} className="block font-display font-semibold text-slate-900 dark:text-white text-sm mt-0.5 line-clamp-2 hover:text-orange-500">
                        {p.title}
                      </Link>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="font-display font-bold text-sm text-slate-900 dark:text-white">{formatPrice(p.price || 0)}</div>
                        <div><MiniScoreArc score={p.product_score} size={72} showLabel={false} /></div>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Composite SibaScore row */}
              <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30">
                <td className="p-4 text-slate-900 dark:text-white font-display font-bold sticky left-0 bg-white dark:bg-slate-900">Composite SibaScore</td>
                {products.map((p) => (
                  <td key={p.product_id} className="p-4 font-display font-bold text-xl text-slate-900 dark:text-white tabular-nums">
                    {p.product_score ?? "—"}<span className="text-xs text-slate-400 font-normal"> / {sibaConfig.bounds.max_score}</span>
                  </td>
                ))}
              </tr>
              {/* Signal rows */}
              {SIGNAL_LABELS.map((sig, i) => {
                const cells = products.map((p) => signalPct(p.product_id, p.product_score, i, sig.label));
                const winner = Math.max(...cells);
                const runnerUp = [...cells].sort((a, b) => b - a)[1] ?? 0;
                const winnersOfThisRow = cells.filter((c) => c === winner).length;
                // "Landslide" — one product beats everyone else by ≥20 pts.
                const landslide = winnersOfThisRow === 1 && (winner - runnerUp) >= 20;
                return (
                  <tr
                    key={sig.key}
                    className={`border-b border-slate-100 dark:border-slate-800 ${
                      landslide ? "bg-emerald-50/60 dark:bg-emerald-950/25" : ""
                    }`}
                    data-testid={`compare-row-${sig.key}`}
                    data-landslide={landslide ? "true" : "false"}
                  >
                    <td className={`p-4 text-slate-600 dark:text-slate-300 sticky left-0 ${landslide ? "bg-emerald-50/95 dark:bg-emerald-950/60" : "bg-white dark:bg-slate-900"}`}>
                      <div className="flex items-center gap-2">
                        <span>{sig.label}</span>
                        {landslide && (
                          <span className="inline-block bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 uppercase tracking-widest font-display">
                            +{winner - runnerUp}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono">{sig.weight}% weight</div>
                    </td>
                    {cells.map((pct, ci) => {
                      const bar = pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-[color:var(--sb-saffron)]" : "bg-amber-500";
                      const isWinner = pct === winner && pct > 0 && winnersOfThisRow === 1;
                      return (
                        <td key={ci} className="p-4">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={`tabular-nums ${isWinner ? "font-bold text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-300"}`}>
                              {pct}{isWinner && " ★"}
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                            <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Price row */}
              <tr className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-4 text-slate-600 dark:text-slate-300 sticky left-0 bg-white dark:bg-slate-900">Price</td>
                {products.map((p) => (
                  <td key={p.product_id} className="p-4 font-display font-bold text-slate-900 dark:text-white tabular-nums">
                    {formatPrice(p.price || 0)}
                  </td>
                ))}
              </tr>
              {/* Category row */}
              <tr className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-4 text-slate-600 dark:text-slate-300 sticky left-0 bg-white dark:bg-slate-900">Category</td>
                {products.map((p) => (
                  <td key={p.product_id} className="p-4 text-slate-500 dark:text-slate-400 capitalize">
                    {p.category || "—"}
                  </td>
                ))}
              </tr>
              {/* View PDP row */}
              <tr>
                <td className="p-4 sticky left-0 bg-white dark:bg-slate-900"></td>
                {products.map((p) => (
                  <td key={p.product_id} className="p-4">
                    <Link
                      to={`/shop/p/${p.slug}`}
                      className="inline-flex items-center gap-1 text-xs bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 font-display font-semibold px-3 py-1.5"
                    >
                      View details
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        </>
      )}
      <QRShareModal
        open={qrOpen}
        onOpenChange={setQrOpen}
        url={shareUrl}
        title={`Compare ${products.length} products`}
        subtitle="Point a phone camera at the QR to load this comparison on the go."
        testIdPrefix="compare-qr"
      />
    </ShopShell>
  );
};

// AI Verdict banner — one-sentence LLM summary of who wins and by how much.
// Kicks off a POST /api/shop/compare/verdict on mount; renders a subtle
// saffron card at the top of the comparison so busy shoppers get the
// answer before they scroll.
const VerdictBanner: React.FC<{ ids: string[] }> = ({ ids }) => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (ids.length < 2) return;
    setError(false);
    setData(null);
    http.post("/shop/compare/verdict", { ids })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [ids.join("~")]);

  if (error) return null;
  return (
    <div
      className="mb-4 border border-[color:var(--sb-saffron)]/40 bg-gradient-to-r from-orange-50 to-white dark:from-orange-950/30 dark:to-slate-900 px-5 py-4 flex items-start gap-3"
      data-testid="compare-verdict-banner"
    >
      <div className="h-8 w-8 shrink-0 flex items-center justify-center bg-[color:var(--sb-saffron)] text-white">
        <Sparkles className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest font-display font-bold text-[color:var(--sb-saffron)]">
          AI verdict {data?.cached && <span className="opacity-60 normal-case tracking-normal">· cached</span>}
        </div>
        {data ? (
          <div className="mt-1 font-serif-display text-base sm:text-lg leading-snug text-slate-900 dark:text-white" data-testid="compare-verdict-text">
            {data.verdict}
          </div>
        ) : (
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400 animate-pulse">
            SibaBaba is looking at the numbers…
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyCompareState: React.FC<{ onBrowse: () => void }> = ({ onBrowse }) => (
  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center" data-testid="compare-empty">
    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-orange-500/10 mb-4">
      <ShoppingBag className="h-6 w-6 text-orange-500" />
    </div>
    <h2 className="font-serif-display font-bold text-xl text-slate-900 dark:text-white">
      Add at least 2 products to compare
    </h2>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
      Tick the "Compare" box on any 2-4 product cards in the shop, then come back here.
    </p>
    <button
      onClick={onBrowse}
      className="mt-5 inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-display font-semibold px-5 py-2.5 transition-colors"
    >
      <ShoppingBag className="h-4 w-4" />
      Browse the shop
    </button>
  </div>
);

export default ComparePage;
